import { useState, useRef, useCallback } from 'react';
import { STARTUP_API_URL } from '../apiConfig';

export interface SearchResult {
  title?: string;
  url?: string;
  description?: string;
  content?: string;
  source?: 'serper' | 'brave' | 'tavily';
}

export type SearchPhase = 'idle' | 'serper' | 'enriched' | 'done' | 'error';

interface UseProgressiveSearchReturn {
  results: SearchResult[];
  phase: SearchPhase;
  activeQuery: string | null;           // which query owns the current results
  sourceCount: Record<string, number>;
  search: (query: string, country?: string, count?: number, enrichTopN?: number) => Promise<void>;
  reset: () => void;
}

function deduplicate(existing: SearchResult[], incoming: SearchResult[]): SearchResult[] {
  const seenUrls = new Set(existing.map(r => (r.url || '').toLowerCase()));
  const seenSnippets = new Set(
    existing
      .map(r => (r.content || r.description || '').slice(0, 120).toLowerCase().trim())
      .filter(Boolean)
  );
  return [...existing, ...incoming.filter(r => {
    const url = (r.url || '').toLowerCase();
    const snippet = (r.content || r.description || '').slice(0, 120).toLowerCase().trim();
    if (url && seenUrls.has(url)) return false;
    if (snippet && seenSnippets.has(snippet)) return false;
    if (url) seenUrls.add(url);
    if (snippet) seenSnippets.add(snippet);
    return true;
  })];
}

export function useProgressiveSearch(): UseProgressiveSearchReturn {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [phase, setPhase] = useState<SearchPhase>('idle');
  const [activeQuery, setActiveQuery] = useState<string | null>(null);
  const [sourceCount, setSourceCount] = useState<Record<string, number>>({});
  const abortRef = useRef<AbortController | null>(null);
  // Monotonically increasing counter — each search() call gets a unique ID.
  // Buffered SSE data from an aborted search can never contaminate a newer search.
  const searchIdRef = useRef(0);

  const reset = useCallback(() => {
    ++searchIdRef.current;
    abortRef.current?.abort();
    setResults([]);
    setPhase('idle');
    setActiveQuery(null);
    setSourceCount({});
  }, []);

  const search = useCallback(async (
    query: string,
    country = 'US',
    count = 20,
    enrichTopN = 5,
  ) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const myId = ++searchIdRef.current;

    // Clear everything and stamp the new query — results will only belong to `query`
    setResults([]);
    setPhase('serper');
    setActiveQuery(query);
    setSourceCount({});

    try {
      const response = await fetch(`${STARTUP_API_URL}/api/v1/search/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken') || '',
        },
        body: JSON.stringify({ query, country, count, enrich_top_n: enrichTopN }),
        signal: controller.signal,
      });

      if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        // If a newer search has started, stop processing — prevent stale results contaminating
        if (searchIdRef.current !== myId) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (searchIdRef.current !== myId) break;
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;
          const jsonStr = trimmed.slice(5).trim();
          if (!jsonStr) continue;

          try {
            const event = JSON.parse(jsonStr) as {
              phase: string;
              results: SearchResult[];
              done: boolean;
            };

            if (event.phase === 'serper') {
              setResults(event.results);
              setPhase('enriched');
              setSourceCount({ serper: event.results.length });
            } else if (event.phase === 'enriched') {
              setResults(prev => deduplicate(prev, event.results));
              setSourceCount(prev => ({
                ...prev,
                brave: event.results.filter(r => r.source === 'brave').length,
                tavily: event.results.filter(r => r.source === 'tavily').length,
              }));
              setPhase('done');
            } else if (event.phase === 'done') {
              setPhase('done');
            }
          } catch {
            // malformed JSON chunk — skip
          }
        }
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      console.error('[useProgressiveSearch] error:', err);
      setPhase('error');
    }
  }, []);

  return { results, phase, activeQuery, sourceCount, search, reset };
}
